import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "../context/AuthContext";

// Rediseño visual alineado con el login de ELICROM IoT.
// Se conservan el endpoint, el filtro por sensores activos y las rutas originales.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores`;

const APPLICATIONS = [
  { label: "Todos los sensores", tipo: "General", featured: true },
  { label: "Humedad y temperatura", tipo: "HumedadTemperatura" },
  { label: "Condiciones Ambientales", tipo: "CondicionesAmbientales" },
  { label: "Cadena de frío GPS", tipo: "HumedadTemperaturaGPS" },
  { label: "Calidad de agua", tipo: "CalidadAgua" },
  { label: "Nivel", tipo: "Nivel" },
  { label: "Temperatura", tipo: "Temperatura" },
  { label: "Flujo", tipo: "Flujo" },
  { label: "Ruido", tipo: "Ruido" },
  { label: "Mapeo térmico", tipo: "MapeoTermico" },
  { label: "GPS", tipo: "GPS" },
  { label: "Presión", tipo: "Presion" },
  { label: "Energía eléctrica", tipo: "EnergiaElectrica" },
  { label: "Estación meteorológica", tipo: "Meteorologica" },
];

// Iconografía SVG de trazo consistente; no requiere instalar bibliotecas ni imágenes nuevas.
function AppIcon({ type }) {
  const common = { width: 35, height: 35, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  const thermo = (
    <>
      <path d="M10 14.8V5a2 2 0 0 1 4 0v9.8a4 4 0 1 1-4 0Z" />
      <path d="M12 9v8" />
    </>
  );
  const pin = (
    <>
      <path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 0 1 14 0Z" />
      <circle cx="12" cy="10" r="2.3" />
    </>
  );
  const cloud = (
    <>
      <path d="M7 18h10a4 4 0 0 0 .2-8 5.7 5.7 0 0 0-10.9 1.5A3.3 3.3 0 0 0 7 18Z" />
      <path d="M5 4l1 1M3 8h1M10 2v2" />
    </>
  );
  let shape;
  switch (type) {
    case "General":
      shape = <><rect x="3" y="3" width="7" height="7" rx="1.4" /><rect x="14" y="3" width="7" height="7" rx="1.4" /><rect x="3" y="14" width="7" height="7" rx="1.4" /><rect x="14" y="14" width="7" height="7" rx="1.4" /></>;
      break;
    case "HumedadTemperatura":
      shape = <><path d="M8 12V5a2 2 0 0 1 4 0v7a4 4 0 1 1-4 0Z" /><path d="M10 9v7" /><path d="M18 6s-3 3.5-3 5a3 3 0 0 0 6 0c0-1.5-3-5-3-5Z" /></>;
      break;
    case "CondicionesAmbientales":
    case "Meteorologica":
      shape = cloud;
      break;
    case "HumedadTemperaturaGPS":
      shape = <>{pin}<path d="M10 10h4" /></>;
      break;
    case "CalidadAgua":
      shape = <><path d="M12 2.5s7 8 7 12a7 7 0 0 1-14 0c0-4 7-12 7-12Z" /><path d="M8.5 15a3.5 3.5 0 0 0 3.5 3.5" /></>;
      break;
    case "Nivel":
      shape = <><path d="M4 4v16M20 4v16M4 20h16" /><path d="M5 13c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 3 2" /></>;
      break;
    case "Temperatura":
      shape = thermo;
      break;
    case "Flujo":
      shape = <><path d="M3 7h13l-3-3m3 3-3 3" /><path d="M21 17H8l3-3m-3 3 3 3" /><path d="M3 12h18" /></>;
      break;
    case "Ruido":
      shape = <><path d="M4 10v4h4l5 4V6l-5 4H4Z" /><path d="M17 9a5 5 0 0 1 0 6M19.5 6.5a8.5 8.5 0 0 1 0 11" /></>;
      break;
    case "MapeoTermico":
      shape = <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18M15 3v18M3 9h18M3 15h18" /><path d="M16 7h.01" strokeWidth="3" /></>;
      break;
    case "GPS":
      shape = pin;
      break;
    case "Presion":
      shape = <><path d="M5.5 18a9 9 0 1 1 13 0" /><path d="m12 13 4-5" /><circle cx="12" cy="13" r="1.2" /><path d="M8 19h8" /></>;
      break;
    case "EnergiaElectrica":
      shape = <path d="M13.5 2 5 13h6l-.5 9L19 10h-6l.5-8Z" />;
      break;
    default:
      shape = thermo;
  }
  return <svg {...common}>{shape}</svg>;
}

function ApplicationCard({ application, onClick, compact }) {
  const { tipo, label, featured } = application;
  return (
    <button
      type="button"
      className={`sts-card${featured ? " sts-card-featured" : ""}${compact ? " sts-card-compact" : ""}`}
      onClick={onClick}
      aria-label={`Abrir ${label}`}
    >
      {featured && <span className="sts-badge">VISTA GENERAL</span>}
      <span className="sts-card-icon"><AppIcon type={tipo} /></span>
      <span className="sts-card-name">{label}</span>
      <span className="sts-card-arrow" aria-hidden="true">→</span>
    </button>
  );
}

function formatRole(role) {
  const normalized = String(role || "").trim().toLowerCase();
  if (normalized === "admin") return "Administrador";
  if (normalized === "supervisor") return "Supervisor";
  if (normalized === "operador") return "Operador";
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Sin rol";
}

export default function SensorTypeSelector() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [availableTypes, setAvailableTypes] = useState(new Set());
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadAvailableTypes = async () => {
      try {
        setLoadingTypes(true);

        const token = localStorage.getItem("token");
        const params = new URLSearchParams(window.location.search);
        const viewAs = params.get("as");
        const url = viewAs
          ? `${API_BASE}/my-sensors?as=${encodeURIComponent(viewAs)}`
          : `${API_BASE}/my-sensors`;

        const { data } = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const types = new Set(
          (Array.isArray(data) ? data : [])
            .filter((sensor) => sensor?.active !== false)
            .map((sensor) => String(sensor?.type || "").trim())
            .filter(Boolean)
        );
        setAvailableTypes(types);
      } catch (error) {
        console.error("Error cargando aplicaciones del usuario:", error);
        if (!cancelled) setAvailableTypes(new Set());
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    };

    loadAvailableTypes();
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleOpenType = (tipo) => {
    try {
      if (tipo === "GPS") return navigate("/gps");
      return navigate(`/dashboard/${tipo}`);
    } catch {
      if (tipo === "GPS") return window.location.assign("/gps");
      return window.location.assign(`/dashboard/${tipo}`);
    }
  };

  // El filtro se mantiene: vista general siempre accesible, otras solo con sensores activos.
  const visibleApplications = APPLICATIONS.filter((app) => {
    if (app.tipo === "General") return true;
    if (loadingTypes) return false;
    return availableTypes.has(app.tipo);
  });
  const compact = visibleApplications.length <= 3;
  const subtitle = visibleApplications.length === 2
    ? "Seleccione una vista para consultar los sensores y datos de su aplicación."
    : "Seleccione una aplicación para visualizar sus sensores y datos disponibles.";

  return (
    <div className="sts-page">
      <style>{SELECTOR_STYLES}</style>

      <header className="sts-header">
        <div className="sts-header-inner">
          <div className="sts-brand" aria-label="ELICROM IoT">
            <span className="sts-brand-logo"><img src="/elicrom-logo.png" alt="ELICROM" /></span>
            <span className="sts-brand-mark">IoT<small>MONITOREO</small></span>
          </div>
          <div className="sts-user-actions">
            <div className="sts-user-info">
              <span className="sts-user-caption">BIENVENIDO</span>
              <strong title={user?.name || user?.username || "Usuario"}>{user?.name || user?.username || "Usuario"}</strong>
              <span>{formatRole(user?.role)} · Equipo {user?.team || "Sin equipo"}</span>
            </div>
            <button type="button" className="sts-logout" onClick={() => logout()}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 17l5-5-5-5"/><path d="M15 12H3"/><path d="M12 3h6a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3h-6"/></svg>
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main className="sts-main">
        <div className="sts-heading">
          <span className="sts-eyebrow">ELICROM IoT · PANEL DE MONITOREO</span>
          <h1>Seleccione una aplicación</h1>
          <p>{subtitle}</p>
        </div>

        {loadingTypes && <p role="status" className="sts-status">Cargando sus aplicaciones disponibles…</p>}

        <div className={`sts-grid${compact ? " sts-grid-compact" : ""}`}>
          {visibleApplications.map((application) => (
            <ApplicationCard
              key={application.tipo}
              application={application}
              compact={compact}
              onClick={() => handleOpenType(application.tipo)}
            />
          ))}
        </div>
      </main>
    </div>
  );
}

// Estilos encapsulados: este archivo JS basta para instalar el rediseño.
const SELECTOR_STYLES = `
.sts-page, .sts-page * { box-sizing: border-box; }
.sts-page {
  min-height: 100vh;
  background: #edf4fa;
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #132a46;
  -webkit-font-smoothing: antialiased;
}
.sts-header { width: 100%; background: #0c2855; border-bottom: 2px solid #3fa3cf; }
.sts-header-inner { max-width: 1180px; min-height: 85px; padding: 10px 26px; margin: auto; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.sts-brand { display: flex; align-items: center; gap: 14px; min-width: 0; }
.sts-brand-logo { display: flex; align-items: center; justify-content: center; width: 154px; height: 56px; flex-shrink: 0; background: #fff; border-radius: 8px; overflow: hidden; }
.sts-brand-logo img { display: block; width: 100%; height: 100%; object-fit: contain; }
.sts-brand-mark { font-size: 22px; font-weight: 850; color: white; letter-spacing: .02em; line-height: 1; }
.sts-brand-mark small { display: block; margin-top: 5px; font-size: 9px; font-weight: 800; letter-spacing: .25em; color: #a9c9e8; }
.sts-user-actions { display: flex; align-items: center; gap: 14px; min-width: 0; }
.sts-user-info { display: flex; flex-direction: column; gap: 3px; border: 1px solid rgba(168,205,241,.19); background: rgba(255,255,255,.065); border-radius: 10px; padding: 8px 15px; min-width: 185px; max-width: 290px; }
.sts-user-caption { color: #8db4de; font-size: 9px; font-weight: 800; letter-spacing: .12em; }
.sts-user-info strong { color: #fff; font-weight: 750; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sts-user-info > span:last-child { color: #c2d5eb; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sts-logout { min-height: 42px; display: inline-flex; align-items: center; gap: 8px; justify-content: center; border: 1px solid rgba(255,255,255,.2); border-radius: 8px; padding: 10px 16px; background: #c53945; color: #fff; font-size: 12px; font-weight: 750; white-space: nowrap; cursor: pointer; transition: background .2s ease, transform .2s ease, box-shadow .2s ease; }
.sts-logout:hover { background: #aa2a39; transform: translateY(-1px); box-shadow: 0 7px 15px rgba(0,0,0,.12); }
.sts-main { max-width: 1180px; margin: auto; padding: 28px 26px 60px; }
.sts-heading { text-align: center; margin: 0 auto 26px; }
.sts-eyebrow { display: inline-block; font-size: 10px; font-weight: 850; color: #2c689f; letter-spacing: .17em; }
.sts-heading h1 { margin: 7px 0 9px; font-size: clamp(27px, 3.3vw, 38px); color: #0c2855; line-height: 1.17; letter-spacing: -.025em; font-weight: 800; }
.sts-heading p { margin: 0; color: #627791; font-size: 14px; line-height: 1.6; }
.sts-status { text-align: center; color: #547797; font-size: 12px; margin: -11px 0 12px; }
.sts-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 17px; width: 100%; }
.sts-grid-compact { grid-template-columns: repeat(auto-fit, minmax(220px, 270px)); justify-content: center; }
.sts-card { position: relative; padding: 22px 13px 17px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 11px; min-height: 151px; background: #fff; border: 1px solid #d5e2ee; border-radius: 14px; box-shadow: 0 5px 15px rgba(12,40,85,.035); color: #163150; font: inherit; text-align: center; cursor: pointer; isolation: isolate; overflow: visible; transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease, background .22s ease; }
.sts-card:hover { transform: translateY(-5px); border-color: #79b4de; background: #fbfdff; box-shadow: 0 15px 30px rgba(12,40,85,.13); }
.sts-card:active { transform: translateY(-1px); }
.sts-card:focus-visible, .sts-logout:focus-visible { outline: 3px solid #55a9e5; outline-offset: 3px; }
.sts-card-featured { border: 1.5px solid #448fc5; background: linear-gradient(155deg, #eef7ff, #fff 83%); box-shadow: 0 8px 23px rgba(32,105,166,.10); }
.sts-card-featured:hover { background: linear-gradient(155deg, #e5f3ff, #fff 90%); }
.sts-badge { position: absolute; top: 10px; right: 10px; font-size: 8px; font-weight: 850; letter-spacing: .085em; color: #1d65a0; background: #ddecfa; border-radius: 4px; padding: 4px 7px; }
.sts-card-icon { width: 60px; height: 60px; flex-shrink: 0; display: grid; place-items: center; border-radius: 16px; color: #1c6ca9; background: #edf5fc; border: 1px solid #e1eef9; transition: color .22s ease, background .22s ease, transform .22s ease; }
.sts-card:hover .sts-card-icon { color: #0b528d; background: #e0f0fe; transform: scale(1.055); }
.sts-card-featured .sts-card-icon { color: #1469a9; background: #e3f1fc; }
.sts-card-name { max-width: 95%; min-height: 20px; display: flex; align-items: center; font-size: 14px; font-weight: 750; line-height: 1.35; }
.sts-card-featured .sts-card-name { color: #0b538c; }
.sts-card-arrow { position: absolute; right: 15px; bottom: 12px; color: #317cba; font-size: 18px; opacity: 0; transform: translateX(-4px); transition: transform .2s ease, opacity .2s ease; }
.sts-card:hover .sts-card-arrow, .sts-card:focus-visible .sts-card-arrow { opacity: 1; transform: translateX(0); }
.sts-card-compact { min-height: 177px; }
@media (max-width: 1000px) { .sts-grid { grid-template-columns: repeat(3, minmax(0,1fr)); } .sts-grid-compact { grid-template-columns: repeat(auto-fit,minmax(220px,270px)); } }
@media (max-width: 750px) { .sts-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } .sts-grid-compact { grid-template-columns: repeat(auto-fit,minmax(190px,270px)); } .sts-header-inner { padding: 13px 18px; } .sts-brand-logo { width: 122px; height: 49px; } .sts-brand-mark { font-size: 19px; } .sts-main { padding: 27px 18px 50px; } .sts-user-info { min-width: 150px; } }
@media (max-width: 570px) { .sts-header-inner { align-items: flex-start; gap: 12px; flex-wrap: wrap; } .sts-user-actions { width: 100%; justify-content: space-between; } .sts-user-info { flex: 1; max-width: none; } .sts-brand-logo { width: 130px; height: 52px; } .sts-grid { grid-template-columns: minmax(0, 1fr); gap: 13px; } .sts-grid-compact { grid-template-columns: minmax(0, 1fr); } .sts-card, .sts-card-compact { min-height: 130px; padding: 14px; } .sts-heading { margin-bottom: 21px; } .sts-heading h1 { font-size: 27px; } .sts-heading p { font-size: 13px; } .sts-card-icon { width: 55px; height: 55px; } .sts-logout { padding: 10px 12px; } }
@media (prefers-reduced-motion: reduce) { .sts-card, .sts-card-icon, .sts-card-arrow, .sts-logout { transition: none; } .sts-card:hover, .sts-logout:hover { transform: none; } }
`;
